/*
  localSoloGame.ts
  Copyright (c) 2024 Human Cube Inc.

  Support for locally calculated solo games.
*/

import cloneDeep from 'clone-deep';

import BF from '../bfcore/bfconst1';
import { store } from '../app/store';
import { aiTurn } from '../bfai/ai_turn';
import { executeTurn, startGame } from '../bfcore/bf_turn';
import { decodeGameString, encodeGameString } from '../bfcore/gameStringDecodeEncode';
import { establishRootState } from '../bfworking/workingUtil';
import { generateSoloGameRandom, SoloGameCreateParams } from '../map/mapGenerate';
import actions from '../reducers/actions';
import mainModes from '../reducers/mainModes';
import { soloGameSave } from './localSoloGameDB';
import { Game, PlayerNormalTurn, PlayerPickTurn } from '../bfcore/gameTypes';
import { generatePickCapital, generatePickZone } from '../bfcore/bf_defaultpick';
import { landWarAfricaGame } from './fixtures/games/landwarAfrica';
import { loadHitmapIntoCache } from '../map/hitmapCache';
import { standardIndianOceanGame } from './fixtures/games/standardIndianOcean';
import { botNames } from '../bfcore/bf_playerfixtures';
import { worldWarIIGame } from './fixtures/games/worldWarII';


function randomLocalSoloGameID (): number {
  // Give it a low value so it does not collide with real gameIDs. TODO: could maybe use a negative number so something else.
  return Math.floor(Math.random() * 1000000) + 1000000;
}

function localSoloGameStart (
  game: Game, localPlayerIndex: number, uid: number, uName: string,
): number {
  // Starts a local solo game specified by game. Register local player and start it.
  // Modified game adds it to redux store, returns the gameID.

  startGame(game);
  soloGameSave(uid, game);  // Save to browser.
  store.dispatch({type: actions.SET_GAME, game, localPlayerIndex});
  const gameStatuses = [decodeGameString(encodeGameString(game))];
  store.dispatch({type: actions.SET_GAME_STATUS, data: gameStatuses});
  store.dispatch({type: actions.GO_HOME});
  return game._id;
}

export function localSoloGameCreateRandom (
  localPlayerIndex: number, params: SoloGameCreateParams,
) {
  // Create a new random map and solo game. Register local player and start it.
  const { uid, uName } = params;
  const game = generateSoloGameRandom(params);
  return localSoloGameStart(game, localPlayerIndex, uid, uName);
}

export function localSoloGameCreateLandWarAfrica (uid: number, uName: string) {
  // Create a new solo game of land war africa and register local player and start it.
  // KEEP IN MIND: Returns before it is actually done due to the loading of the hitmap.
  const localPlayerIndex = 1;
  const game = cloneDeep(landWarAfricaGame);
  game._id = randomLocalSoloGameID();
  game.flags |= BF.GAME_FLAG_SOLO_GAME |
                BF.GAME_FLAG_DO_NOT_SCORE |
                BF.GAME_FLAG_CLIENT_CALCULATED;
  game.playerID[localPlayerIndex] = uid;
  game.playerName[localPlayerIndex] = uName;

  loadHitmapIntoCache(game.mapID, game.mapFlags, game.mapXS, game.mapYS, game.mapData,
    (err: any) => {
      if(err) {
        console.log('Hitmap loading for map failed.');
        return;
      }
      localSoloGameStart(game, localPlayerIndex, uid, uName);
    }
  );
}

function localSoloGameCreateFromGame (
  uid: number, uName: string, localPlayerIndex: number, sourceGame: Game,
) {
  // KEEP IN MIND: Returns before it is actually done due to the loading of the hitmap.
  const game = cloneDeep(sourceGame);
  game._id = randomLocalSoloGameID();
  game.flags |= BF.GAME_FLAG_SOLO_GAME |
                BF.GAME_FLAG_DO_NOT_SCORE |
                BF.GAME_FLAG_CAN_CHOOSE_PLAYER_INDEX |
                BF.GAME_FLAG_CLIENT_CALCULATED;
  game.humanPlayers = 1;
  game.computerPlayers = game.players - game.humanPlayers;

  for(let p = 1; p <= game.players; p++) {
    if(p === localPlayerIndex) {
      game.playerFlags[p] = BF.PLAYER_FLAG_HUMAN;
      game.playerID[p] = uid;
      game.playerName[p] = uName;
    }
    else {
      game.playerFlags[p] = BF.PLAYER_FLAG_COMPUTER;
      game.playerID[p] = -p;
      game.playerName[p] = botNames[p];
    }
  }

  loadHitmapIntoCache(game.mapID, game.mapFlags, game.mapXS, game.mapYS, game.mapData,
    (err: any) => {
      if(err) {
        console.log('Hitmap loading for map failed.');
        return;
      }
      localSoloGameStart(game, localPlayerIndex, uid, uName);
    }
  );
}

export function localSoloGameCreateStandardIndianOcean (uid: number, uName: string) {
  // Create a new indian ocean solo game and register local player and start it.
  // KEEP IN MIND: Returns before it is actually done due to the loading of the hitmap.
  const localPlayerIndex = Math.floor(Math.random() * standardIndianOceanGame.players) + 1;
  localSoloGameCreateFromGame(uid, uName, localPlayerIndex, standardIndianOceanGame);
}

export function localSoloGameCreateWorldWarII (
  uid: number, uName: string, localPlayerIndex: number,
) {
  // Create a new ww2 solo game and register local player and start it.
  // KEEP IN MIND: Returns before it is actually done due to the loading of the hitmap.
  localSoloGameCreateFromGame(uid, uName, localPlayerIndex, worldWarIIGame);
}

export const localSoloGameCalculate = (
  game: Game,
  uid: number,
  localPlayerIndex: number,
  playersTurn: PlayerPickTurn | PlayerNormalTurn,
) => {
  // For client side only SOLO games, progress the game with local calculations only.
  const turns = [];
  for(let p = 1; p <= game.players; p++) {
    if(game.required & (1 << p)) {
      if(p === localPlayerIndex) {
        turns[p] = playersTurn;
      }
      else {
        const thisPlayerWorking = establishRootState(game, p);
        turns[p] = aiTurn(thisPlayerWorking, game, true);
      }
    }
  }
  const nextGame = cloneDeep(game);
  executeTurn(nextGame, turns);

  // For picking turns, continue to execute turns until it is the local player's turn:
  while(nextGame.turn >= BF.TURN_PICK_ZONE && nextGame.turn <= BF.TURN_PICK_CAPITAL) {
    if(nextGame.required & (1 << localPlayerIndex)) {
      break;  // It is local player's turn, so stop advancing turns and let them play.
    }
    const turns = [];
    for(let p = 1; p <= nextGame.players; p++) {
      if(nextGame.required & (1 << p)) {
        if(nextGame.turn === BF.TURN_PICK_CAPITAL) {
          turns[p] = generatePickCapital(nextGame, p);
        }
        else {
          turns[p] = generatePickZone(nextGame, p);
        }
      }
    }
    executeTurn(nextGame, turns);
  }

  soloGameSave(uid, nextGame);  // Save to browser.
  store.dispatch({type: actions.SET_GAME, game: nextGame, localPlayerIndex});
  const gameStatuses = [decodeGameString(encodeGameString(nextGame))];
  store.dispatch({type: actions.SET_GAME_STATUS, data: gameStatuses});
  store.dispatch({type: actions.SET_MAIN_MODE, mode: mainModes.GAME_REPLAY});
};
